'use client';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { TeamGrid, TeamGridItem } from '@sapientpro/sapientpro-data-models';
import { useAtomValue } from 'jotai';
import styles from './TeamValues.module.scss';
import AnimatedTitle from '../AnimatedTitle';
import TeamValuesGrid from './TeamValuesGrid';
import TeamValuesSlider from './TeamValuesSlider';
import ReviewOverlay from './ReviewOverlay';
import deviceScreenSize, { ScreenSize } from '../../store/deviceScreenSize';

const TeamValues = ({ data }: { data: TeamGrid }) => {
  const screenSize = useAtomValue(deviceScreenSize);
  const [activeItem, setActiveItem] = useState<TeamGridItem | null>(null);
  const [showReviewOverlay, setShowReviewOverlay] = useState<boolean>(false);
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (sectionRef.current) {
      const sectionRect = sectionRef.current.getBoundingClientRect();

      const offsetDiff = sectionRect.y - 200;
      const bottomOffsetDiff = sectionRect.y + sectionRect.height;

      if (offsetDiff < 0) {
        if (bottomOffsetDiff < 200) {
          sectionRef.current.classList.remove(styles.active);
        } else {
          sectionRef.current.classList.add(styles.active);
        }
      }
    }
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChangeActiveItem = (newItem: TeamGridItem | null) => {
    if (!newItem) {
      setActiveItem(null);
      setShowReviewOverlay(false);
    } else {
      setActiveItem(newItem);
      !showReviewOverlay && setShowReviewOverlay(true);

      if (hoveredItemId !== newItem.id) {
        setHoveredItemId(newItem.id);
      }
    }
  };

  const handleHoverToggle = (itemId: number | null) => {
    if (activeItem) return;
    setHoveredItemId(itemId);
  };

  const isMobile = screenSize < ScreenSize.TABLET_PORTRAIT;
  const showDesktopOverlay = showReviewOverlay && !!activeItem && !isMobile;

  return (
    <section
      className={styles.teamValues}
      ref={sectionRef}
    >
      <div className={styles.teamValuesBackground} />
      <div className={classNames(styles.teamValuesContent, 'container', 'home-page')}>
        <div className={styles.text}>
          <h2 className={styles.title}><AnimatedTitle title={data.title} /></h2>
          <p className={styles.description}>{classNames(data.description, 'bodyLarge')}</p>
        </div>
        <div className={styles.contentWrapper}>
          <TeamValuesGrid
            items={data.items}
            changeActive={handleChangeActiveItem}
            handleHover={handleHoverToggle}
            hoveredId={hoveredItemId}
          />
          {showDesktopOverlay && (
          <ReviewOverlay
            key={activeItem?.video?.url}
            item={activeItem}
            close={() => handleChangeActiveItem(null)}
          />
          )}
        </div>
      </div>
      <TeamValuesSlider
        items={data.items}
        changeActive={handleChangeActiveItem}
        handleHover={handleHoverToggle}
        activeId={activeItem?.id}
      />
    </section>
  );
};

export default TeamValues;
